import React, { useContext, useEffect, useId, useState } from "react";
import JSZip from "jszip";
import { AuthContext } from "../../context/AuthContext";
import ActionButton from "../../components/button/ActionButton";
import GeneralInformation2 from "../../components/generalInformation2/GeneralInformation2";
import { QRCodeSVG } from "qrcode.react";
import desktopIcon from "../../assets/2/desktopIcon.svg";
import mobileIcon from "../../assets/2/mobileIcon.svg";
import tabletIcon from "../../assets/2/tabletIcon.svg";
import inappIcon from "../../assets/2/inappIcon.svg";
import desktopMobileIcon from "../../assets/2/desktopMobileIcon.svg";
import desktopTabletIcon from "../../assets/2/desktopTabletIcon.svg";
import desktopMobileTabletIcon from "../../assets/2/desktopMobileTabletIcon.svg";
import PublisherIcon2 from "../publisherIcon2/PublisherIcon2";
import styles from "./Sheet2.module.css";

const Sheet2 = ({
  specsheet,
  generalInformation,
  toggleShowModifyForm,
}) => {
  const { user } = useContext(AuthContext);
  const [device, setDevice] = useState(0);
  const [templateImageIndex, setTemplateImageIndex] = useState(0);

  const deviceIcons = {
    desktop: desktopIcon,
    mobile: mobileIcon,
    tablet: tabletIcon,
    inapp: inappIcon,
    desktopmobile: desktopMobileIcon,
    desktoptablet: desktopTabletIcon,
    desktopmobiletablet: desktopMobileTabletIcon,
  };

  const faqSections = [
    { title: "This template", items: [["Can I use a video?", "Yes, you can use a video."], ["On which websites will this template be used?", "This template can be used on any site."]] },
    { title: "General", items: [["When do I need to notify this creative at the latest?", "5 days due live date."], ["Can assets be shared across elements?", "Yes. Share assets where possible to reduce the total creative weight."]] },
  ];
  const fallbackDownloads = [
    { id: "template", name: "Template package", link: "#" },
  ];

  const creatives = specsheet.creatives;
  const previewLinks = specsheet.previews;
  const previewPages = specsheet.previewPages;
  const additionalInformation = specsheet.additionalTemplateInformation;

  const downloadLinks = (specsheet.downloadFormat || []).filter((item) => item.link);
  const downloads = downloadLinks.map((item, index) => ({
    id: `${item.type || "download"}-${index}`,
    name: item.type || `Template package ${index + 1}`,
    link: item.link,
  }));
  const availableDownloads = downloads.length ? downloads : fallbackDownloads;

  const templateImages = creatives.flatMap((creative) => (creative.images || []).filter((image) => image?.url));
  const templateVisualTitle = `${specsheet.category?.toLowerCase() === "standard" ? "Standard" : specsheet.publisher} ${specsheet.templateName}`.trim();
  const currentTemplateImage = templateImages[templateImageIndex % Math.max(templateImages.length, 1)];

  function Dialog({ title, close, children }) {
    return (
      <div className={styles.backdrop} onClick={close}>
        <section className={styles.dialog} role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
          <header className={styles.dialogHeader}>
            <h2>{title}</h2>
            <button type="button" onClick={close} aria-label="Close dialog">×</button>
          </header>
          {children}
        </section>
      </div>
    );
  }

  function InfoTooltip({ content, label }) {
    const tooltipId = useId();
    if (!content?.trim()) return null;

    return (
      <span className={styles.infoTooltip}>
        <button type="button" className={styles.infoTooltipTrigger} aria-label={`More information about ${label}`} aria-describedby={tooltipId}>i</button>
        <span id={tooltipId} className={styles.infoTooltipContent} role="tooltip" dangerouslySetInnerHTML={{ __html: content }} />
      </span>
    );
  }

  useEffect(() => {
    setTemplateImageIndex(0);
    if (templateImages.length < 2) return undefined;

    const imageRotation = window.setInterval(() => {
      setTemplateImageIndex((index) => (index + 1) % templateImages.length);
    }, 5000);

    return () => window.clearInterval(imageRotation);
  }, [templateImages.length]);

  const devices = creatives.length
    ? creatives.map((creative, index) => {
      const iconType = (creative.creativeIcon).toLowerCase();
      const icon = deviceIcons[iconType];
      return {
        id: index,
        label: creative.creative,
        icon,
        creative,
      };
    })
    : [{ id: 0, label: "Desktop", icon: deviceIcons.desktop, creative: null }];
  const selectedDevice = devices[Math.min(device, devices.length - 1)];

  const adPositionData = selectedDevice?.creative?.adPositions ?? selectedDevice?.creative?.adPosition ?? [];
  const adPositions = Array.isArray(adPositionData) ? adPositionData : [adPositionData];
  const fileRows = adPositions.flatMap((position, positionIndex) =>
    (position.positionsList || []).map((item, fileIndex) => {
      const fields = (item.details || []).flatMap((detail) => detail.infoFields || []);
      const dimensions = fields
        .filter((field) => field.subject?.toLowerCase().includes("dimension"))
        .map((field) => field.text)
        .filter(Boolean)
        .join(", ");
      const additionalInfo = fields
        .filter((field) => /additional\s*info|additinal\s*info/i.test(field.subject || ""))
        .map((field) => field.text)
        .filter(Boolean)
        .join("<br />");

      return {
        id: `${positionIndex}-${fileIndex}`,
        name: item.htmlName || item.title || `File ${fileIndex + 1}`,
        dimensions,
        extraInformation: item.extraInformation || additionalInfo,
      };
    })
  );
  const positionGroups = [
    { type: "fixedPosition", label: "FIXED_POSITION" },
    { type: "layerPosition", label: "LAYER" },
    { type: "mediaFile", label: "MEDIA_FILE" },
  ].filter(({ type }) => adPositions.some((position) => (position.positionsList || []).some((item) => item.type === type)));

  const creativeDetailData = selectedDevice?.creative?.details ?? selectedDevice?.creative?.details ?? [];
  const creativeDetails = Array.isArray(creativeDetailData) ? creativeDetailData : [creativeDetailData];

  const [dialog, setDialog] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [downloadNotice, setDownloadNotice] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [versionUrl, setVersionUrl] = useState("");
  const [versionNotice, setVersionNotice] = useState("");
  const [expandedFaqs, setExpandedFaqs] = useState(["This template", "General"]);

  function toggleFile(fileName) {
    setSelectedFiles((files) => files.includes(fileName) ? files.filter((item) => item !== fileName) : [...files, fileName]);
    setDownloadNotice("");
  }

  function toggleFaq(title) {
    setExpandedFaqs((sections) => sections.includes(title) ? sections.filter((item) => item !== title) : [...sections, title]);
  }

  async function startDownloads() {
    const filesToDownload = availableDownloads.filter((item) => selectedFiles.includes(item.id) && item.link !== "#");
    if (!filesToDownload.length) {
      setDownloadNotice("Select at least one downloadable file.");
      return;
    }

    setIsDownloading(true);
    setDownloadError("");
    setDownloadNotice(`Preparing ${filesToDownload.length} file${filesToDownload.length === 1 ? "" : "s"}…`);

    try {
      if (filesToDownload.length === 1) {
        const [item] = filesToDownload;
        const response = await fetch(item.link);
        if (!response.ok) throw new Error(`${item.name}: HTTP ${response.status}`);

        const url = new URL(item.link, window.location.href);
        const extension = url.pathname.match(/\.[a-z0-9]{1,8}$/i)?.[0] || "";
        const fileName = `${(item.name || "download").replace(/[<>:"/\\|?*]/g, "_")}${extension}`;
        const fileUrl = URL.createObjectURL(await response.blob());
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
        setDownloadNotice(`Downloaded ${item.name}.`);
        return;
      }

      const zip = new JSZip();
      const usedNames = new Set();

      for (const [index, item] of filesToDownload.entries()) {
        const response = await fetch(item.link);
        if (!response.ok) throw new Error(`${item.name}: HTTP ${response.status}`);

        const data = await response.arrayBuffer();
        let extractedZip;
        try {
          extractedZip = await JSZip.loadAsync(data, { checkCRC32: true });
        } catch (error) {
          throw new Error(`${item.name}: ${error.message}`);
        }

        const sanitizedName = (item.name || "").replace(/\.zip$/i, "").replace(/[<>:"/\\|?*]/g, "_").trim();
        const baseName = sanitizedName && !/^\.+$/.test(sanitizedName) ? sanitizedName : `download-${index + 1}`;
        let folderName = baseName;
        let suffix = 2;
        while (usedNames.has(folderName.toLowerCase())) {
          folderName = `${baseName}-${suffix++}`;
        }
        usedNames.add(folderName.toLowerCase());
        const folder = zip.folder(folderName);

        for (const entry of Object.values(extractedZip.files)) {
          if (entry.dir) {
            folder.folder(entry.name);
          } else {
            folder.file(entry.name, await entry.async("uint8array"), { date: entry.date });
          }
        }
      }

      const archive = await zip.generateAsync({ type: "blob" });
      const archiveUrl = URL.createObjectURL(archive);
      const link = document.createElement("a");
      const archiveName = `${
        specsheet.category.toLowerCase() === "standard"
          ? specsheet.category
          : specsheet.publisher.replace(/ /g, "_")
      }-${specsheet.website !== "" ? `${specsheet.website}-` : ""}${specsheet.templateName}-downloads.zip`
        .replace(/[<>:"/\\|?*\s]/g, "_");

      link.href = archiveUrl;
      link.download = archiveName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(archiveUrl), 1000);
      setDownloadNotice(`Downloaded ZIP containing ${filesToDownload.length} extracted download folders.`);
    } catch (error) {
      setDownloadNotice("");
      setDownloadError(error instanceof TypeError
        ? "Could not download the selected files. Please check your internet connection and try again."
        : `Could not create ZIP: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  }

  function displayModificationForm() {
    toggleShowModifyForm(true);
  }

  return (
    <div className={styles.page}>
      {user?.role === "admin" && (
        <section className={styles.modifyButtonSection}>
          <ActionButton iconName="edit" onClick={displayModificationForm}>Modify specsheet</ActionButton>
        </section>
      )}
      <main>
        <section className={styles.hero}>
          <div className={styles.purpleOrb} aria-hidden="true" />
          <div className={styles.heroInner}>
            {templateImages.length > 0 && <div className={styles.templateVisual}>
              <div className={styles.templateImage}>
                <img className={styles.templateExampleImage} src={currentTemplateImage.url} alt={`${templateVisualTitle} example ${templateImageIndex + 1}`} />
                <strong>{templateVisualTitle}</strong>
              </div>
            </div>}

            <p className={styles.category}>
              {(
                specsheet.category.toLowerCase() === "standard"
                  ? specsheet.category
                  : <PublisherIcon2 publisher={specsheet.publisher} />
              )}
            </p>

            <h1 className={styles.templateName}>
              {(
                specsheet.website !== ""
                  ? `${specsheet.website} ${specsheet.templateName}`
                  : `${specsheet.templateName}`
              )}
            </h1>

            <p className={styles.kicker}>
              {specsheet.environment}
              { specsheet.device !== "" && ` - ${specsheet.device}`}
              { specsheet.section !== "" && ` - ${specsheet.section}`}
            </p>
            <p className={styles.updated}>Last update: {specsheet.lastUpdate}</p>

            <div className={styles.mainActions}>
              <ActionButton iconName="download" onClick={() => setDialog("download")}>Download</ActionButton>
              <ActionButton iconName="eye" onClick={() => setDialog("preview")}>Preview</ActionButton>
            </div>

            <div className={styles.utilityActions}>
              <button type="button" onClick={() => setDialog("version")}>Template Version Check</button>
              <button type="button" onClick={() => setDialog("previewer")}>Previewer <span>➤</span></button>
              <button type="button" onClick={() => setDialog("changelog")}>Changelog <span>△</span></button>
            </div>

            <GeneralInformation2 name={specsheet.templateName} />
          </div>
        </section>

        <section className={styles.intro}>
          <h2>Template introduction</h2>
          <p>{additionalInformation}</p>
        </section>

        <section className={styles.specification} aria-label="Template specifications">
          <div className={styles.deviceTabs} role="tablist" aria-label="Device type">
            {devices.map(({ id, label, icon }, index) => <button type="button" role="tab" aria-selected={index === device} className={index === device ? styles.activeTab : ""} onClick={() => setDevice(index)} key={`${id}-${label}`}><img src={icon} alt="" aria-hidden="true" />{label}</button>)}
          </div>
          <div className={styles.specBody}>
            <section className={styles.filesSection}>
              <h2>Files information</h2>
              <div className={styles.filesTableWrap}>
                <table className={styles.filesTable}>
                  <thead>
                    <tr>
                      <th scope="col">File name</th>
                      <th scope="col">Dimensions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileRows.length ? fileRows.map((file) => (
                      <tr key={file.id}>
                        <td>
                          <div className={styles.fileNameCell}>
                            <strong>{file.name}</strong>
                            <p>{file.extraInformation}</p>
                            {/* <InfoTooltip content={file.extraInformation} label={file.name} /> */}
                          </div>
                        </td>
                        <td>{file.dimensions || "-"}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="2">No files listed for this device.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            <section className={styles.adPositionsSection}>
              <h2>Ad Position information</h2>
              <p>If you are working with this template in our Creation platform, please review the ad positions carefully.</p>
              {adPositions.length > 0 && <div className={styles.adPositionGrid} role="group" aria-label="Ad positions">
                {adPositions.map((position, index) => {
                  const label = position.position || position.title || `AdPosition ${index + 1}`;
                  return <article className={styles.adPositionCard} key={`${label}-${index}`}>
                    <div className={styles.adPositionCardHeader}>
                      <small>Ad Position {index + 1}</small>
                      <strong>{label}</strong>
                    </div>
                    <div className={styles.adPositionCardBody}>
                      {positionGroups.map(({ type, label: groupLabel }) => {
                        const files = (position.positionsList || []).filter((item) => item.type === type);
                        return <section className={styles.positionFileGroup} key={type}>
                          <h3>{groupLabel}</h3>
                          {files.map((item, fileIndex) => {
                            const fields = (item.details || []).flatMap((detail) => detail.infoFields || []);
                            const dimensions = fields.filter((field) => field.subject?.toLowerCase().includes("dimension")).map((field) => field.text).filter(Boolean).join(", ");
                            const weight = fields.filter((field) => field.subject?.toLowerCase().includes("weight")).map((field) => field.text).filter(Boolean).join(", ");
                            // const additionalInfo = fields.filter((field) => /additional\s*info|additinal\s*info/i.test(field.subject || "")).map((field) => field.text).filter(Boolean).join("<br />");
                            // const fileExtraInformation = item.extraInformation || additionalInfo;
                            const metadata = [dimensions, weight].filter(Boolean).join(" • ");
                            return <div className={styles.positionFile} key={`${item.htmlName || item.title}-${fileIndex}`}>
                              <div className={styles.positionFileHeading}>
                                <strong>{item.htmlName || item.title}</strong>
                                {/* <InfoTooltip content={fileExtraInformation} label={item.htmlName || item.title || "file"} /> */}
                              </div>
                              {metadata && <small>{metadata}</small>}
                            </div>;
                          })}
                        </section>;
                      })}
                    </div>
                  </article>;
                })}
              </div>}
            </section>
            <section className={styles.templateInfo}>
              <h2>Template information</h2>
              <h3>General template guidelines</h3>
              {(
                specsheet.additionalTemplateInformation !== ""
                ? (
                  specsheet.additionalTemplateInformation.toLowerCase().indexOf('!important:') !== -1
                  ? <p><strong>!important:</strong> {specsheet.additionalTemplateInformation.replace('!important:', '')}</p>
                  : <p>{specsheet.additionalTemplateInformation}</p>
                )
                : ""
              )}
              {/* <p>{generalInformation.heavyAd?.ideas?.general0}</p> */}
              <div role="group" aria-label="Creative Assets">
                <article>
                  <h4>Creative Assets</h4>
                  <ul>
                    <li><strong>Weight</strong>: 300KB/element max (except video and wallpaper)</li>
                    <li><strong>Type</strong>: Image, fonts, libraries, etc. (Please share assets across elements to save MB's)</li>
                    <li><strong>Position in previewer</strong>: Asset</li></ul>
                  <h4>Video File (optional asset)</h4>
                  <ul>
                    <li><strong>Encoding</strong>: MP4</li>
                    <li><strong>Size</strong>: Element with video needs to be under 4MB total including other creative assets used by that element (15 seconds max)</li>
                    <li><strong>Position in previewer</strong>: Asset</li>
                    <li><strong>Additional Info</strong>: On Volkskrant, Trouw, and Parool, the video and animation time is limited to a maximum of 5 seconds.</li>
                    <a href="https://support.Adways.nl/hc/en-us/articles/115001323832-Custom-Video-Controls" target="_blank" rel="noreferrer">Custom Controls Documentation</a>
                  </ul>
                </article>
              </div>
              {/* <p>{generalInformation.heavyAd?.general1}.</p> */}

              <h3>Format specific guidelines</h3>
              {creativeDetails.length > 0 && <div className="" role="group" aria-label="Creative Details">
                {adPositions[0].extraInformation.length > 0 && <div
                  dangerouslySetInnerHTML={{ __html: adPositions[0].extraInformation }}
                   key="a"
                ></div>}
                {creativeDetails.map((creativeDetail, index) => {
                  const creativeDetailTitle = creativeDetail.title;
                  return <article className="" key={`${index}-${creativeDetailTitle}`}>
                    {creativeDetail.infoFields.length > 0 && <h4>{creativeDetailTitle}</h4>}
                    {creativeDetail.infoFields.length > 0 && <ul>
                      {creativeDetail.infoFields.map((infoField, index) => {
                        const returnValue = (
                          infoField.subject !== ""
                            ? <li key={`${index}-${infoField.subject}`}><strong>{infoField.subject}</strong>: {infoField.text}</li>
                            : <a href={infoField.url} target="_blank" rel="noreferrer" key={`${index}-${infoField.subject}`}>{infoField.text}</a>
                        );
                        return returnValue;
                      })}
                    </ul>}
                  </article>;
                })}
              </div>}
            {/* </section>
            <section className={styles.previewInstructions}> */}
              {/* <h2>Template preview instructions for developers</h2> */}
              <h3>Template preview instructions for developers</h3>
              {/* <p>{generalInformation.testing?.general}</p> */}
              <h4>Preview Pages</h4>
              {previewPages.length ? previewPages.map((page, index) => <ul key={`${page.title}-${index}`}>
                <li><strong>{page.title}</strong>: {(page.websites || []).map((site) => site.site).filter(Boolean).join(", ") || "No preview pages listed."}</li>
              </ul>) : <><strong>Preview page</strong><p>No preview pages listed.</p></>}
            </section>
          </div>
        </section>

        <section className={styles.faq} id="faq">
          <h2>FAQ</h2>
          {faqSections.map(({ title, items }) => <div className={styles.faqSection} key={title}>
            <button type="button" onClick={() => toggleFaq(title)} aria-expanded={expandedFaqs.includes(title)}>{title}<span>{expandedFaqs.includes(title) ? "⌃" : "⌄"}</span></button>
            {expandedFaqs.includes(title) && items.map(([question, answer]) => <details key={question} open><summary>{question}</summary><p>{answer}</p></details>)}
          </div>)}
        </section>
      </main>

      {dialog === "download" && <Dialog title="Download template" close={() => setDialog("")}>
        <p className={styles.dialogIntro}>Choose the files you need for your creative.</p>
        <div className={styles.downloadList}>
          {availableDownloads.map((item) => (
            <label key={item.id}>
              <input type="checkbox" disabled={isDownloading || item.link === "#"} checked={selectedFiles.includes(item.id)} onChange={() => toggleFile(item.id)} />
              <span><strong>{item.name}</strong><small>{item.link === "#" ? "No download provided" : "Template package"}</small></span>
            </label>
          ))}
        </div>
        {downloadNotice && <p className={styles.success}>{downloadNotice}</p>}
        {downloadError && <p className={styles.error}>{downloadError}</p>}
        <ActionButton iconName="download" onClick={startDownloads}>
          {isDownloading ? "Preparing download..." : selectedFiles.length === 1 ? "Download selected file" : "Download selected as ZIP"}
        </ActionButton>
      </Dialog>}

      {dialog === "preview" && <Dialog title="Preview template" close={() => setDialog("")}>
        <p className={styles.dialogIntro}>Scan a code or choose a device preview.</p>
        <div className={styles.previewList}>{
            previewLinks.length
            ? previewLinks.map((preview, index) => (
            <a href={preview.link} target="_blank" rel="noreferrer" key={`${preview.type}-${index}`}>
              <span className={styles.previewQr}>{preview.qr && preview.link ? <QRCodeSVG value={preview.link} size={72} /> : <img src={selectedDevice?.icon} alt="" aria-hidden="true" />}</span><strong>{preview.type || "Preview"}</strong><small>Open preview</small>
            </a>
            ))
            : <p>No preview links are available for this template.</p>
        }</div>
      </Dialog>}

      {dialog === "version" && <Dialog title="Template Version Check" close={() => setDialog("")}>
        <p className={styles.dialogIntro}>Paste a preview URL to compare your creative with the latest template.</p>
        <form className={styles.versionForm} onSubmit={(event) => { event.preventDefault(); setVersionNotice(versionUrl ? "Your creative is ready to compare with the latest template." : "Enter a preview URL to continue."); }}>
          <label htmlFor="version-url">Preview URL</label><input id="version-url" type="url" value={versionUrl} onChange={(event) => setVersionUrl(event.target.value)} placeholder="https://example.com/preview" />
          {versionNotice && <p className={styles.success}>{versionNotice}</p>}<button className={styles.dialogPrimary} type="submit">Check version</button>
        </form>
      </Dialog>}

      {dialog === "previewer" && <Dialog title="Previewer" close={() => setDialog("")}><p className={styles.dialogIntro}>Upload your creative to review each device placement before delivery.</p><button className={styles.dialogPrimary} type="button" onClick={() => setDialog("preview")}>Choose a device preview</button></Dialog>}

      {dialog === "changelog" && <Dialog title="Changelog" close={() => setDialog("")}><div className={styles.change}><time>{specsheet.lastUpdate}</time><strong>{specsheet.templateName}</strong><p>{specsheet.additionalTemplateInformation}</p></div></Dialog>}
    </div>
  );
};

export default Sheet2;
